import classNames from "classnames";
import { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import fallbackDestinationUrl from "assets/images/fallback-destination.svg?url";
import { useBreakpoint } from "hooks/use-breakpoint";
import { useSelector } from "hooks/use-selector";
import { selectResidencyState, selectTripDays } from "store/quote/selectors";
import { type PlaceDetails } from "types/quote-types";
import { LocalTimezoneDate } from "utils/date";
import { dateRangeFormatter } from "utils/formatters/date-range";
import { type CurrencyParts, numberFormatter } from "utils/formatters/number";
import { getGooglePlacesDetails } from "utils/google-places/get-details";
import { sentryCaptureError } from "utils/tracking/sentry";
import { getCancelUntilDate } from "../utils";
import {
  CancellationTag,
  CancellationToolTip,
  Container,
  Content,
  ContentColumns,
  Destination,
  DestinationContainer,
  DestinationMore,
  Details,
  InfoIcon,
  Picture,
  PictureContainer,
  Price,
  ProfileLogo,
  Subtitle,
} from "./styles";

export interface DestinationSectionProps {
  amendPayment?: boolean;
  amountParts: CurrencyParts | null;
  destinations?: PlaceDetails[];
  travelersCount: number;
  tripStartDate?: string;
  tripEndDate?: string;
  userPolicyUniqueUuid?: string;
}

export const DestinationSection = forwardRef<
  HTMLDivElement,
  DestinationSectionProps
>(function DestinationSection(
  {
    amendPayment = false,
    amountParts,
    destinations,
    travelersCount,
    tripEndDate,
    tripStartDate,
    userPolicyUniqueUuid,
  },
  ref,
) {
  const { t } = useTranslation("screens", {
    keyPrefix: "checkout.sections.destination",
  });
  const { t: tCheckout } = useTranslation("screens", {
    keyPrefix: "checkout",
  });

  const mdBreakpoint = useBreakpoint("md", { noSsr: true });
  const days = useSelector(selectTripDays);
  const stateOfResidence = useSelector(selectResidencyState);
  const cancelUntilDate = getCancelUntilDate(tripStartDate, stateOfResidence);
  const [pictureSrc, setPictureSrc] = useState<string>();

  useEffect(() => {
    function handleResize() {
      if (!destinations?.length || !mdBreakpoint || pictureSrc) return;

      getGooglePlacesDetails({
        fields: ["photos"],
        placeId: destinations[0].googleId,
      })
        .then(({ photos }) => {
          setPictureSrc(
            photos?.[0]?.getUrl({ maxWidth: 960 }) || fallbackDestinationUrl,
          );
        })
        .catch((error) => {
          sentryCaptureError(error, {
            contexts: { destination: { ...destinations[0] } },
            name: "Destination_photo_load_failed",
          });
          setPictureSrc(fallbackDestinationUrl);
        });
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.addEventListener("resize", handleResize);
    };
  }, [mdBreakpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container className={classNames({ amendPayment })} ref={ref}>
      {!amendPayment && (
        <PictureContainer>
          {pictureSrc && (
            <Picture
              alt=""
              src={pictureSrc}
              onError={() => {
                setPictureSrc(fallbackDestinationUrl);
              }}
            />
          )}
        </PictureContainer>
      )}

      <ProfileLogo />

      <Content>
        <Subtitle className="days">{t("days", { count: days || 1 })}</Subtitle>

        <ContentColumns>
          <Details>
            {!!destinations?.length && (
              <DestinationContainer>
                <Destination>
                  {destinations[0].label}
                  {destinations[0].country === "US"
                    ? `, ${destinations[0].country} `
                    : " "}
                </Destination>
                {destinations.length > 1 && (
                  <DestinationMore>
                    {t("moreThanOneDestination", {
                      count: destinations.length - 1,
                    })}
                  </DestinationMore>
                )}
              </DestinationContainer>
            )}

            {amendPayment && (
              <Subtitle className="policyId">{userPolicyUniqueUuid}</Subtitle>
            )}

            <Subtitle>
              {!!tripStartDate &&
                !!tripEndDate &&
                dateRangeFormatter(
                  new LocalTimezoneDate(tripStartDate),
                  new LocalTimezoneDate(tripEndDate),
                )}
            </Subtitle>

            <Subtitle className="light">
              {t("travelers", { count: travelersCount })}
            </Subtitle>

            {cancelUntilDate && !amendPayment && (
              <CancellationToolTip
                content={tCheckout("cancelUntilTooltip", {
                  date: cancelUntilDate,
                })}
                contentProps={{
                  align: "center",
                  side: "top",
                  sideOffset: 5,
                }}
                fullWidth="trigger"
                variant="secondary"
              >
                <CancellationTag>
                  <InfoIcon />
                  {tCheckout("cancelUntilTag", { date: cancelUntilDate })}
                </CancellationTag>
              </CancellationToolTip>
            )}
          </Details>

          {amountParts && (
            <Price>
              <span className="currency">{amountParts.currency}</span>
              {numberFormatter(parseInt(amountParts.integer))}
              <span className="fraction">
                {amountParts.decimalSeparator}
                {amountParts.fraction}
              </span>
              {amendPayment && <span className="info">{t("price.amend")}</span>}
            </Price>
          )}
        </ContentColumns>
      </Content>
    </Container>
  );
});
